<script setup lang="ts">
import type { GuideExperienceProfile } from '@/onboarding/types'
import useOnboardingStore from '@/store/modules/onboarding'
import { useI18n } from 'vue-i18n'

const onboardingStore = useOnboardingStore()
const { locale, t } = useI18n()

function useArchiveChinese() {
  return locale.value.toLowerCase() === 'zh-cn'
}

const setupTitle = computed(() => {
  const keys = [
    'onboarding.setup.titleWelcome',
    'onboarding.setup.titleExperience',
    'onboarding.setup.titleGoals',
    'onboarding.setup.titlePlan',
  ]
  return t(keys[onboardingStore.setupStep], { edition: t(onboardingStore.edition.nameKey) })
})

const experienceLabel = computed(() => onboardingStore.setupExperience
  ? t(`onboarding.experience.${onboardingStore.setupExperience}.label`)
  : t('onboarding.experience.new.label'))

function selectSetupExperience(experienceId: GuideExperienceProfile) {
  onboardingStore.setupExperience = experienceId
}

function toggleSetupGoal(goalId: string) {
  const current = onboardingStore.setupGoals
  onboardingStore.setupGoals = current.includes(goalId)
    ? current.filter(id => id !== goalId)
    : [...current, goalId]
}

function handleBeforeClose() {
  onboardingStore.deferWelcome()
}
</script>

<template>
  <el-dialog
    v-model="onboardingStore.welcomeVisible"
    class="onboarding-setup-dialog app-layer-body-exception"
    modal-class="onboarding-guide-overlay"
    width="720px"
    append-to-body
    align-center
    :close-on-click-modal="false"
    :show-close="false"
    :before-close="handleBeforeClose"
  >
    <template #header>
      <div class="onboarding-setup-header">
        <div class="onboarding-setup-title-wrap">
          <span class="onboarding-eyebrow">
            {{ t(onboardingStore.edition.nameKey) }} · {{ t('onboarding.setup.guideLabel') }}
          </span>
          <h2>{{ setupTitle }}</h2>
        </div>
        <el-button
          class="onboarding-close"
          :aria-label="t('onboarding.common.closeAndRemind')"
          @click="onboardingStore.deferWelcome"
        >
          <FaIcon name="i-ph:x" />
        </el-button>
      </div>
    </template>

    <div class="onboarding-setup-body">
      <div
        class="onboarding-step-dots"
        :aria-label="t('onboarding.setup.stepLabel', { current: onboardingStore.setupStep + 1, total: 4 })"
      >
        <span
          v-for="index in 4"
          :key="index"
          :class="{ 'is-active': index - 1 === onboardingStore.setupStep }"
        />
        <em>{{ t('onboarding.setup.stepLabel', { current: onboardingStore.setupStep + 1, total: 4 }) }}</em>
      </div>

      <template v-if="onboardingStore.setupStep === 0">
        <div class="onboarding-welcome">
          <div class="onboarding-welcome-copy">
            <h3>{{ t('onboarding.setup.welcomeHeading') }}</h3>
            <p>{{ t('onboarding.setup.welcomeHint') }}</p>
            <div class="onboarding-promise-grid">
              <article v-for="item in ['safe', 'resume', 'targeted']" :key="item" class="onboarding-promise-card">
                <strong>{{ t(`onboarding.setup.promises.${item}.title`) }}</strong>
                <span>{{ t(`onboarding.setup.promises.${item}.description`) }}</span>
              </article>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="onboardingStore.setupStep === 1">
        <h3 class="onboarding-question-title">
          {{ t('onboarding.setup.experienceQuestion') }}
        </h3>
        <p class="onboarding-question-hint">
          {{ t('onboarding.setup.experienceHint') }}
        </p>
        <div class="onboarding-choice-list" role="radiogroup">
          <button
            v-for="option in onboardingStore.edition.experiences"
            :key="option.id"
            type="button"
            class="onboarding-choice"
            :class="{ 'is-selected': onboardingStore.setupExperience === option.id }"
            role="radio"
            :aria-checked="onboardingStore.setupExperience === option.id"
            @click="selectSetupExperience(option.id)"
          >
            <span class="onboarding-choice-radio" aria-hidden="true" />
            <span class="onboarding-choice-copy">
              <strong>
                {{ useArchiveChinese() ? option.label : t(`onboarding.experience.${option.id}.label`) }}
                <span v-if="option.recommended" class="onboarding-badge">
                  {{ t('onboarding.common.recommended') }}
                </span>
              </strong>
              <small>{{ useArchiveChinese() ? option.hint : t(`onboarding.experience.${option.id}.hint`) }}</small>
            </span>
          </button>
        </div>
      </template>

      <template v-else-if="onboardingStore.setupStep === 2">
        <h3 class="onboarding-question-title">
          {{ t('onboarding.setup.goalsQuestion') }}
        </h3>
        <p class="onboarding-question-hint">
          {{ t('onboarding.setup.goalsHint') }}
        </p>
        <div class="onboarding-goal-grid" role="group">
          <button
            v-for="goal in onboardingStore.edition.goals"
            :key="goal.id"
            type="button"
            class="onboarding-goal"
            :class="{ 'is-selected': onboardingStore.setupGoals.includes(goal.id) }"
            role="checkbox"
            :aria-checked="onboardingStore.setupGoals.includes(goal.id)"
            @click="toggleSetupGoal(goal.id)"
          >
            <span class="onboarding-goal-check" aria-hidden="true">
              {{ onboardingStore.setupGoals.includes(goal.id) ? '✓' : '' }}
            </span>
            <span class="onboarding-goal-copy">
              <strong>{{ useArchiveChinese() ? goal.label : t(`onboarding.goals.${goal.id}`) }}</strong>
              <small>{{ useArchiveChinese() ? goal.hint : t(`onboarding.goalHints.${goal.id}`) }}</small>
            </span>
          </button>
        </div>
      </template>

      <template v-else>
        <h3 class="onboarding-question-title">
          {{ t('onboarding.setup.planHeading', { edition: t(onboardingStore.edition.nameKey) }) }}
        </h3>
        <p class="onboarding-question-hint">
          {{ t('onboarding.setup.planHint', { experience: experienceLabel }) }}
        </p>
        <div class="onboarding-route-map">
          <article
            v-for="(task, index) in onboardingStore.setupRecommendedTasks"
            :key="task.id"
            class="onboarding-route-item"
            :class="{ 'is-current': index === 0 }"
          >
            <span class="onboarding-route-number">{{ String(index + 1).padStart(2, '0') }}</span>
            <strong>{{ useArchiveChinese()
              ? task.title
              : t(task.titleKey, { edition: t(onboardingStore.edition.nameKey) }) }}</strong>
            <small>{{ t('onboarding.setup.routeMeta', {
              count: task.steps.length,
              description: useArchiveChinese() ? task.description : t(task.descriptionKey),
            }) }}</small>
          </article>
        </div>
        <div class="onboarding-safe-note">
          <strong>{{ t('onboarding.setup.safeTitle') }}</strong>
          {{ t('onboarding.setup.safeDescription') }}
        </div>
      </template>
    </div>

    <template #footer>
      <div class="onboarding-setup-footer">
        <template v-if="onboardingStore.setupStep === 0">
          <el-button class="onboarding-btn-text" text @click="onboardingStore.deferWelcome">
            {{ t('onboarding.common.remindLater') }}
          </el-button>
          <el-button class="onboarding-btn-secondary" @click="onboardingStore.quickStart">
            {{ t('onboarding.common.quickStart') }}
          </el-button>
          <el-button class="onboarding-btn-primary" type="primary" @click="onboardingStore.nextSetup">
            {{ t('onboarding.common.startSetup') }}
          </el-button>
        </template>

        <template v-else-if="onboardingStore.setupStep < 3">
          <el-button class="onboarding-btn-secondary" @click="onboardingStore.previousSetup">
            {{ t('onboarding.common.previous') }}
          </el-button>
          <el-button
            class="onboarding-btn-primary"
            type="primary"
            :disabled="onboardingStore.setupStep === 1
              ? !onboardingStore.setupExperience
              : !onboardingStore.setupGoals.length"
            @click="onboardingStore.nextSetup"
          >
            {{ onboardingStore.setupStep === 2
              ? t('onboarding.common.generateRoute')
              : onboardingStore.setupExperience === 'expert'
                ? t('onboarding.common.selectGoals')
                : t('onboarding.common.next') }}
          </el-button>
        </template>

        <template v-else>
          <el-button class="onboarding-btn-secondary" @click="onboardingStore.previousSetup">
            {{ t('onboarding.common.adjust') }}
          </el-button>
          <el-button
            class="onboarding-btn-secondary"
            :loading="onboardingStore.remoteSyncing"
            @click="onboardingStore.completeSetup(true)"
          >
            {{ t('onboarding.common.viewTaskCenter') }}
          </el-button>
          <el-button
            class="onboarding-btn-primary"
            type="primary"
            :loading="onboardingStore.remoteSyncing"
            @click="onboardingStore.completeSetup(false)"
          >
            {{ onboardingStore.setupExperience === 'expert'
              ? t('onboarding.common.openChecklist')
              : t('onboarding.common.startFirstTask') }}
          </el-button>
        </template>
      </div>
    </template>
  </el-dialog>
</template>

<style>
/* Reference package tokens for the four-step onboarding setup dialog. */
.onboarding-guide-overlay {
  background: rgb(15 23 42 / 52%) !important;
  backdrop-filter: blur(2px) !important;
}

html body .onboarding-setup-dialog.el-dialog {
  --onboarding-ref-primary: #2563eb;
  --onboarding-ref-primary-hover: #1d4ed8;
  --onboarding-ref-primary-soft: #eff6ff;
  --onboarding-ref-card: #fff;
  --onboarding-ref-border: #e5eaf2;
  --onboarding-ref-border-strong: #cbd5e1;
  --onboarding-ref-border-muted-blue: #d7e3f4;
  --onboarding-ref-border-primary-soft: #bfd3ff;
  --onboarding-ref-surface-cool: #fbfdff;
  --onboarding-ref-text: #111827;
  --onboarding-ref-title: #0f2a58;
  --onboarding-ref-text-body: #334155;
  --onboarding-ref-text-secondary: #64748b;
  box-sizing: border-box;
  width: min(720px, calc(100vw - 48px)) !important;
  height: min(560px, calc(100dvh - 48px));
  max-height: min(560px, calc(100dvh - 48px));
  padding: 0 !important;
  margin: auto !important;
  overflow: hidden !important;
  color: var(--onboarding-ref-text);
  background: var(--onboarding-ref-card) !important;
  border: 1px solid var(--onboarding-ref-border) !important;
  border-radius: 16px !important;
  box-shadow: 0 22px 60px rgb(15 23 42 / 22%) !important;
}

html body .onboarding-setup-dialog.el-dialog > .el-dialog__header {
  box-sizing: border-box;
  height: auto !important;
  min-height: 64px !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden;
  background: var(--onboarding-ref-card) !important;
  border-bottom: 1px solid var(--onboarding-ref-border) !important;
  border-radius: 16px 16px 0 0 !important;
}

html body .onboarding-setup-dialog.el-dialog > .el-dialog__body {
  box-sizing: border-box;
  height: calc(100% - 128px);
  min-height: 0;
  padding: 0 !important;
  overflow: hidden;
  background: var(--onboarding-ref-card) !important;
}

html body .onboarding-setup-dialog.el-dialog > .el-dialog__footer {
  box-sizing: border-box;
  height: 64px !important;
  min-height: 64px !important;
  max-height: 64px !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden;
  background: var(--onboarding-ref-card) !important;
  border-top: 1px solid var(--onboarding-ref-border) !important;
  border-radius: 0 0 16px 16px !important;
}

html body .onboarding-setup-header {
  box-sizing: border-box;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100%;
  min-height: 64px !important;
  padding: 12px 20px !important;
  margin: 0 !important;
  background: var(--onboarding-ref-card) !important;
}

.onboarding-setup-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.onboarding-eyebrow {
  color: var(--onboarding-ref-primary);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0.02em;
}

html body .onboarding-setup-header h2 {
  margin: 0 !important;
  color: var(--onboarding-ref-title) !important;
  font-size: 18px !important;
  font-weight: 700 !important;
  line-height: 24px !important;
}

html body .onboarding-setup-dialog .onboarding-close {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px !important;
  min-width: 32px !important;
  height: 32px !important;
  min-height: 32px !important;
  padding: 0 !important;
  margin: 0 !important;
  color: var(--onboarding-ref-text-secondary) !important;
  background: var(--onboarding-ref-card) !important;
  border: 1px solid var(--onboarding-ref-border) !important;
  border-radius: 10px !important;
  box-shadow: none !important;
}

html body .onboarding-setup-dialog .onboarding-close:hover {
  color: var(--onboarding-ref-primary) !important;
  background: var(--onboarding-ref-primary-soft) !important;
  border-color: var(--onboarding-ref-border-primary-soft) !important;
}

html body .onboarding-setup-body {
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  padding: 20px 24px 22px !important;
  overflow: auto;
  background: var(--onboarding-ref-card) !important;
  scrollbar-gutter: stable;
}

html body .onboarding-setup-footer {
  box-sizing: border-box;
  display: flex !important;
  gap: 8px !important;
  align-items: center !important;
  justify-content: flex-end !important;
  width: 100%;
  min-height: 64px !important;
  padding: 12px 20px !important;
  margin: 0 !important;
  background: var(--onboarding-ref-card) !important;
}

.onboarding-step-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 16px;
}

.onboarding-step-dots span {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--onboarding-ref-border-muted-blue);
  transition: width 0.18s ease, background-color 0.18s ease;
}

.onboarding-step-dots span.is-active {
  width: 24px;
  background: var(--onboarding-ref-primary);
}

.onboarding-step-dots em {
  color: var(--onboarding-ref-text-secondary);
  font-size: 12px;
  font-style: normal;
  line-height: 18px;
}

.onboarding-welcome {
  width: min(100%, 650px);
  min-height: 0;
  padding: 0;
  margin: 0 auto;
}

.onboarding-welcome-copy {
  width: 100%;
  margin: 0;
  text-align: left;
}

.onboarding-welcome-copy h3,
.onboarding-question-title {
  margin: 0;
  color: var(--onboarding-ref-title);
  font-size: 24px;
  font-weight: 700;
  line-height: 34px;
}

.onboarding-welcome-copy > p,
.onboarding-question-hint {
  margin: 8px 0 0;
  color: var(--onboarding-ref-text-secondary);
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
}

/* Step 1 */
.onboarding-promise-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 28px auto 0;
}

.onboarding-promise-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  min-width: 0;
  min-height: 82px;
  padding: 14px 18px;
  background: var(--onboarding-ref-card);
  border: 1px solid var(--onboarding-ref-border);
  border-radius: 14px;
}

.onboarding-promise-card strong {
  color: var(--onboarding-ref-text-body);
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
  text-align: left;
}

.onboarding-promise-card span {
  color: var(--onboarding-ref-text-secondary);
  font-size: 12px;
  line-height: 18px;
  text-align: left;
}

/* Step 2: use the same plain-card structure as the reference package. */
.onboarding-choice-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  max-width: 650px;
  margin: 28px auto 0;
}

.onboarding-choice {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  min-height: 82px;
  padding: 14px 18px;
  margin: 0;
  font-family: inherit;
  color: var(--onboarding-ref-text);
  text-align: left;
  background: var(--onboarding-ref-card);
  border: 1px solid var(--onboarding-ref-border);
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 0.16s ease, background-color 0.16s ease, box-shadow 0.16s ease;
}

.onboarding-choice:hover {
  background: var(--onboarding-ref-surface-cool);
  border-color: var(--onboarding-ref-border-primary-soft);
}

.onboarding-choice.is-selected {
  background: var(--onboarding-ref-primary-soft);
  border-color: var(--onboarding-ref-primary);
  box-shadow: 0 0 0 3px rgb(37 99 235 / 8%);
}

.onboarding-choice-radio {
  box-sizing: border-box;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  background: var(--onboarding-ref-card);
  border: 2px solid var(--onboarding-ref-border-strong);
  border-radius: 999px;
  box-shadow: inset 0 0 0 4px var(--onboarding-ref-card);
}

.onboarding-choice.is-selected .onboarding-choice-radio {
  background: var(--onboarding-ref-primary);
  border-color: var(--onboarding-ref-primary);
}

.onboarding-choice-copy,
.onboarding-goal-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.onboarding-choice-copy strong,
.onboarding-goal-copy strong {
  color: var(--onboarding-ref-text-body);
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}

.onboarding-choice-copy small,
.onboarding-goal-copy small {
  color: var(--onboarding-ref-text-secondary);
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.onboarding-badge {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 7px;
  margin-left: 8px;
  color: var(--onboarding-ref-primary);
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  vertical-align: middle;
  background: var(--onboarding-ref-primary-soft);
  border-radius: 999px;
}

/* Step 3 */
.onboarding-goal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 26px;
}

.onboarding-goal {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  width: 100%;
  min-height: 96px;
  padding: 16px 18px 16px 48px;
  margin: 0;
  font-family: inherit;
  text-align: left;
  background: var(--onboarding-ref-card);
  border: 1px solid var(--onboarding-ref-border);
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 0.16s ease, background-color 0.16s ease;
}

.onboarding-goal:hover,
.onboarding-goal.is-selected {
  background: var(--onboarding-ref-primary-soft);
  border-color: var(--onboarding-ref-border-primary-soft);
}

.onboarding-goal.is-selected {
  border-color: var(--onboarding-ref-primary);
}

.onboarding-goal-check {
  position: absolute;
  top: 50%;
  left: 16px;
  box-sizing: border-box;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  color: var(--onboarding-ref-card);
  font-size: 13px;
  line-height: 1;
  background: var(--onboarding-ref-card);
  border: 1px solid var(--onboarding-ref-border-strong);
  border-radius: 7px;
  transform: translateY(-50%);
}

.onboarding-goal.is-selected .onboarding-goal-check {
  background: var(--onboarding-ref-primary);
  border-color: var(--onboarding-ref-primary);
}

/* Step 4 */
.onboarding-route-map {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 24px;
}

.onboarding-route-item {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-height: 118px;
  padding: 18px 16px 16px;
  background: var(--onboarding-ref-surface-cool);
  border: 1px solid var(--onboarding-ref-border);
  border-radius: 14px;
}

.onboarding-route-item.is-current {
  background: var(--onboarding-ref-primary-soft);
  border-color: var(--onboarding-ref-border-primary-soft);
}

.onboarding-route-number {
  position: absolute;
  top: 10px;
  right: 12px;
  color: var(--onboarding-ref-border-strong);
  font-size: 20px;
  font-weight: 700;
}

.onboarding-route-item.is-current .onboarding-route-number {
  color: rgb(37 99 235 / 30%);
}

.onboarding-route-item strong {
  color: var(--onboarding-ref-text-body);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.onboarding-route-item small {
  color: var(--onboarding-ref-text-secondary);
  font-size: 12px;
  line-height: 18px;
}

.onboarding-safe-note {
  padding: 11px 13px;
  margin-top: 16px;
  color: var(--onboarding-ref-text-body);
  font-size: 12px;
  line-height: 20px;
  background: #fff7ed;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

/* Reference-package buttons inside setup footer. */
html body .onboarding-setup-dialog .onboarding-btn-primary,
html body .onboarding-setup-dialog .onboarding-btn-secondary,
html body .onboarding-setup-dialog .onboarding-btn-text {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto !important;
  min-width: 0 !important;
  min-height: 36px !important;
  height: 36px !important;
  padding: 0 18px !important;
  margin: 0 !important;
  font-family: inherit;
  font-size: 14px !important;
  font-weight: 500 !important;
  line-height: 1 !important;
  border-radius: 10px !important;
  cursor: pointer;
  transition: border-color 0.16s ease, background-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;
}

html body .onboarding-setup-dialog .onboarding-btn-primary {
  color: #fff !important;
  background: var(--onboarding-ref-primary) !important;
  border: 1px solid var(--onboarding-ref-primary) !important;
  box-shadow: 0 5px 14px rgb(37 99 235 / 16%) !important;
}

html body .onboarding-setup-dialog .onboarding-btn-primary:hover {
  background: var(--onboarding-ref-primary-hover) !important;
  border-color: var(--onboarding-ref-primary-hover) !important;
}

html body .onboarding-setup-dialog .onboarding-btn-secondary {
  color: var(--onboarding-ref-text-body) !important;
  background: var(--onboarding-ref-card) !important;
  border: 1px solid var(--onboarding-ref-border-strong) !important;
  box-shadow: none !important;
}

html body .onboarding-setup-dialog .onboarding-btn-secondary:hover {
  color: var(--onboarding-ref-primary) !important;
  background: var(--onboarding-ref-primary-soft) !important;
  border-color: var(--onboarding-ref-border-primary-soft) !important;
}

html body .onboarding-setup-dialog .onboarding-btn-text {
  padding-inline: 10px !important;
  color: var(--onboarding-ref-text-secondary) !important;
  background: transparent !important;
  border: 1px solid transparent !important;
  box-shadow: none !important;
}

html body .onboarding-setup-dialog .onboarding-btn-text:hover {
  color: var(--onboarding-ref-primary) !important;
  background: #f8fafc !important;
}

html body .onboarding-setup-dialog .onboarding-btn-primary:disabled,
html body .onboarding-setup-dialog .onboarding-btn-primary.is-disabled,
html body .onboarding-setup-dialog .onboarding-btn-secondary:disabled,
html body .onboarding-setup-dialog .onboarding-btn-secondary.is-disabled {
  color: #94a3b8 !important;
  background: #f8fafc !important;
  border-color: var(--onboarding-ref-border) !important;
  box-shadow: none !important;
  cursor: not-allowed;
}

.onboarding-choice:focus-visible,
.onboarding-goal:focus-visible,
html body .onboarding-setup-dialog .el-button:focus-visible {
  outline: 2px solid #93c5fd;
  outline-offset: 2px;
}

@media (width <= 767px) {
  html body .onboarding-setup-dialog.el-dialog {
    width: calc(100vw - 16px - env(safe-area-inset-left) - env(safe-area-inset-right)) !important;
    height: calc(100dvh - 16px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
    max-height: calc(100dvh - 16px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  }

  html body .onboarding-setup-dialog.el-dialog > .el-dialog__body {
    height: calc(100% - 128px);
  }

  html body .onboarding-setup-body {
    padding: 18px 16px 22px !important;
  }

  html body .onboarding-setup-header,
  html body .onboarding-setup-footer {
    padding-inline: 16px !important;
  }

  .onboarding-welcome-copy h3,
  .onboarding-question-title {
    font-size: 20px;
    line-height: 28px;
  }

  .onboarding-goal-grid,
  .onboarding-route-map {
    grid-template-columns: 1fr;
  }

  .onboarding-route-item {
    min-height: 86px;
  }

  html body .onboarding-setup-dialog .el-button {
    padding-inline: 10px !important;
  }
}

@media (prefers-reduced-motion: reduce) {
  .onboarding-step-dots span,
  .onboarding-choice,
  .onboarding-goal {
    transition: none;
  }
}
</style>
